import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/layout/AppShell";
import { AlertTriangle, CheckCircle2, Info, XCircle, Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  notificacionesService,
  type NotificationItem,
  type NotificationType,
} from "@/services/notificaciones.service";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/notificaciones")({
  head: () => ({ meta: [{ title: "Notificaciones — LicitaPro" }] }),
  component: Page,
});

const icons: Record<NotificationType, { I: typeof Info; c: string }> = {
  warning: { I: AlertTriangle, c: "bg-warning/15 text-warning" },
  success: { I: CheckCircle2, c: "bg-success/15 text-success" },
  info: { I: Info, c: "bg-info/15 text-info" },
  error: { I: XCircle, c: "bg-destructive/15 text-destructive" },
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? "ayer" : `hace ${d} días`;
}

function Page() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["notifications", "licita"],
    queryFn: () => notificacionesService.list(false),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["notifications"] });

  const markRead = useMutation({
    mutationFn: notificacionesService.markRead,
    onSuccess: invalidate,
  });
  const markAll = useMutation({
    mutationFn: notificacionesService.markAllRead,
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: notificacionesService.remove, onSuccess: invalidate });

  const items: NotificationItem[] = data?.notifications ?? [];
  const noLeidas = items.filter((n) => !n.isRead).length;

  return (
    <AppShell title="Centro de Notificaciones" subtitle={`${noLeidas} sin leer`}>
      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-elegant max-w-3xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <span className="text-sm font-medium text-foreground">
            {data?.total ?? items.length} notificaciones
          </span>
          {noLeidas > 0 && (
            <button
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="text-xs text-primary hover:underline disabled:opacity-50"
            >
              Marcar todas como leídas
            </button>
          )}
        </div>

        {isLoading && (
          <div className="p-6 text-sm text-muted-foreground">Cargando notificaciones…</div>
        )}
        {isError && (
          <div className="p-6 text-sm text-muted-foreground">
            No se pudieron cargar las notificaciones del core.
          </div>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">No tienes notificaciones.</div>
        )}

        <div className="divide-y divide-border">
          {items.map((n) => {
            const cfg = icons[n.type] ?? icons.info;
            return (
              <div
                key={n.id}
                className={cn(
                  "flex items-start gap-3 p-4 hover:bg-muted/40 transition",
                  !n.isRead && "bg-primary/[0.03]",
                )}
              >
                <div className={cn("size-9 rounded-md grid place-items-center shrink-0", cfg.c)}>
                  <cfg.I className="size-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={cn("text-sm", !n.isRead && "font-semibold")}>{n.title}</span>
                    {!n.isRead && <span className="size-1.5 rounded-full bg-primary" />}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {timeAgo(n.createdAt)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!n.isRead && (
                    <button
                      onClick={() => markRead.mutate(n.id)}
                      className="text-xs text-primary hover:underline"
                    >
                      Marcar leída
                    </button>
                  )}
                  <button
                    onClick={() => remove.mutate(n.id)}
                    className="text-muted-foreground hover:text-destructive transition"
                    title="Eliminar"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
